import { CreditRuleRequestFor, type CriteriaType, type RequirementType, type RuleSetFormData } from '../types';

export function copyRuleSetPath(eventId: string): string {
  return `/api/v1/cpe/events/${eventId}/rulesets/copy`;
}

export function rulesetRulePath(ruleSetId: string, ruleId: string): string {
  return `/api/v1/cpe/rulesets/${ruleSetId}/rules/${ruleId}`;
}

export interface CopyRuleSetRequest {
  template_id: string;
}

export type ApiEntityType = 'EVENT' | 'TEMPLATE' | 'SERIES' | 'REPEATING_EVENT';
export type ApiDistributionLevel = 'PER_EVENT' | 'PER_SESSION';
export type ApiCriteriaType =
  | 'TIME_SPENT'
  | 'POLLS_ANSWERED'
  | 'CTA_CLICKED'
  | 'SURVEY_COMPLETED'
  | 'RESOURCES_DOWNLOADED'
  | 'QNA_ANSWERED';
export type ApiOperator = 'GREATER_THAN' | 'GREATER_THAN_OR_EQUAL' | 'EQUAL';

export interface CreateRuleCriteriaRequest {
  criteria_type: ApiCriteriaType;
  operator: ApiOperator;
  threshold_value: number;
}

export interface CreateRuleRequest {
  credits_awarded: number;
  cert_template_id: string;
  email_template_id: string;
  criteria: CreateRuleCriteriaRequest[];
}

export interface CreateRuleSetRequest {
  entity_type: ApiEntityType;
  entity_id: string;
  enabled: boolean;
  rules: CreateRuleRequest[];
}

export interface PatchRuleRequest {
  distribution_level: ApiDistributionLevel;
  applicable_session_ids?: string[];
}

export interface RuleCriteriaResponse {
  id: string;
  rule_id: string;
  criteria_type: ApiCriteriaType;
  operator: ApiOperator;
  threshold_value: number;
}

export interface RuleResponse {
  id: string;
  rule_set_id: string;
  credits_awarded: number;
  distribution_level?: ApiDistributionLevel;
  applicable_session_ids: string[];
  cert_template_id?: string;
  email_template_id?: string;
  sort_order: number;
  created_at: string;
  criteria: RuleCriteriaResponse[];
}

export interface RuleSetResponse {
  id: string;
  org_id: string;
  entity_type: ApiEntityType;
  entity_id: string;
  distribution_level?: ApiDistributionLevel;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  rules: RuleResponse[];
}

const CRITERIA_TYPE_TO_API: Record<CriteriaType, ApiCriteriaType> = {
  user_timespent_event: 'TIME_SPENT',
  poll_responses: 'POLLS_ANSWERED',
  in_app_survey: 'SURVEY_COMPLETED',
  cta_clicked: 'CTA_CLICKED',
  resources_downloaded: 'RESOURCES_DOWNLOADED',
  qna_answered: 'QNA_ANSWERED'
};

const API_TO_CRITERIA_TYPE: Record<ApiCriteriaType, CriteriaType> = {
  TIME_SPENT: 'user_timespent_event',
  POLLS_ANSWERED: 'poll_responses',
  SURVEY_COMPLETED: 'in_app_survey',
  CTA_CLICKED: 'cta_clicked',
  RESOURCES_DOWNLOADED: 'resources_downloaded',
  QNA_ANSWERED: 'qna_answered'
};

const REQUIREMENT_TO_OPERATOR: Record<RequirementType, ApiOperator> = {
  at_least: 'GREATER_THAN_OR_EQUAL',
  exactly: 'EQUAL'
};

const OPERATOR_TO_REQUIREMENT: Record<ApiOperator, RequirementType> = {
  GREATER_THAN_OR_EQUAL: 'at_least',
  GREATER_THAN: 'at_least',
  EQUAL: 'exactly'
};

export function entityTypeForRequestFor(requestFor: CreditRuleRequestFor): ApiEntityType {
  return requestFor === CreditRuleRequestFor.Template ? 'TEMPLATE' : 'EVENT';
}

export function toRuleSetRequest(
  entityId: string,
  requestFor: CreditRuleRequestFor,
  data: RuleSetFormData
): CreateRuleSetRequest {
  const rules: CreateRuleRequest[] = data.rules.map(rule => {
    const criteria: CreateRuleCriteriaRequest[] = rule.criteria
      .filter((c): c is typeof c & { type: CriteriaType } => Boolean(c.type))
      .map(c => ({
        criteria_type: CRITERIA_TYPE_TO_API[c.type],
        operator: REQUIREMENT_TO_OPERATOR[c.requirement],
        threshold_value: Number(c.value)
      }));
    return {
      credits_awarded: Number(rule.creditAmount),
      cert_template_id: data.certificate,
      email_template_id: data.emailDesign,
      criteria
    };
  });

  return {
    entity_type: entityTypeForRequestFor(requestFor),
    entity_id: entityId,
    enabled: true,
    rules
  };
}

export function toPatchRuleRequest(rule: RuleSetFormData['rules'][number]): PatchRuleRequest | null {
  if (rule.appliesToEvent) {
    return { distribution_level: 'PER_EVENT' };
  }
  if (rule.appliesToSessions) {
    return { distribution_level: 'PER_SESSION', applicable_session_ids: rule.sessions ?? [] };
  }
  return null;
}

export function fromRuleSetResponse(response: RuleSetResponse): RuleSetFormData {
  const firstRule = (response.rules ?? [])[0];
  return {
    rules: (response.rules ?? []).map(rule => ({
      creditAmount: String(rule.credits_awarded),
      appliesToEvent: rule.distribution_level === 'PER_EVENT',
      appliesToSessions: rule.distribution_level === 'PER_SESSION',
      sessions: rule.applicable_session_ids ?? [],
      criteria: (rule.criteria ?? []).map(c => ({
        type: API_TO_CRITERIA_TYPE[c.criteria_type] ?? '',
        requirement: OPERATOR_TO_REQUIREMENT[c.operator] ?? 'at_least',
        value: String(c.threshold_value)
      }))
    })),
    certificate: firstRule?.cert_template_id ?? '',
    emailDesign: firstRule?.email_template_id ?? ''
  };
}
